import React, { useState } from 'react';
import { PermitLetter } from '../../types';
import Button from '../Form/Button';
import Input from '../Form/Input';
import RichTextEditor from '../Form/RichTextEditor';
import { resolveFileUrl } from '../../utils/fileUrl';

interface ImmigrationLetterEditModalProps {
  letter: PermitLetter;
  /** Tokenized default body used when the stored content has no tokens yet. */
  defaultBody: string;
  onSave: (data: Partial<PermitLetter> & { logoFile?: File }) => Promise<void>;
  onClose: () => void;
}

const PLACEHOLDERS = [
  { token: '{{DATE}}', label: 'التاريخ' },
  { token: '{{CHANGE_DATE}}', label: 'تاريخ الـ Crew Change' },
  { token: '{{COMPANY_NAME}}', label: 'اسم الشركة' },
  { token: '{{PASSPORT_OFFICE}}', label: 'مكتب الجوازات' },
  { token: '{{VESSEL_NAME}}', label: 'اسم الوحدة' },
  { token: '{{FLAG}}', label: 'العلم' },
  { token: '{{OWNER_NATIONALITY}}', label: 'جنسية المالك' },
  { token: '{{FROM_PORT}}', label: 'ميناء القدوم' },
  { token: '{{CURRENT_LOCATION}}', label: 'المتواجدة حاليا' },
  { token: '{{COUNT}}', label: 'عدد البحارة' },
  { token: '{{EMPLOYEE_TABLE}}', label: 'جدول البحارة' },
  { token: '{{EMPLOYEE_NAMES}}', label: 'قائمة أسماء البحارة' },
];

const ImmigrationLetterEditModal: React.FC<ImmigrationLetterEditModalProps> = ({
  letter,
  defaultBody,
  onSave,
  onClose,
}) => {
  const [templateName, setTemplateName] = useState(letter.templateName);
  const [subject, setSubject] = useState(letter.subject);
  // Older seeded content has no tokens, so it can't drive generation —
  // start those from the tokenized default instead.
  const [content, setContent] = useState(
    letter.content && letter.content.includes('{{') ? letter.content : defaultBody
  );
  const [headerHtml, setHeaderHtml] = useState(letter.headerHtml || '');
  const [footerHtml, setFooterHtml] = useState(letter.footerHtml || '');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    if (!templateName.trim() || !subject.trim() || !content.trim()) {
      setError('Template name, subject and content are required (كل الحقول مطلوبة)');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await onSave({ templateName: templateName.trim(), subject: subject.trim(), content, headerHtml, footerHtml, logoFile: logoFile || undefined });
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Failed to save template');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 sticky top-0 bg-white z-10 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-900">Edit Letter Template (تعديل الخطاب)</h2>
          <Button type="button" variant="secondary" onClick={onClose}>Close</Button>
        </div>

        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label="Template Name (اسم القالب)"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
            />
            <Input
              label="Subject (الموضوع)"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
            الرموز مثل {'{{PASSPORT_OFFICE}}'} تُستبدل تلقائياً بالبيانات المدخلة عند إنشاء الخطاب —
            استخدم أزرار الرموز أدناه لإدراجها في النص.
          </div>

          <RichTextEditor
            label="Letter Content (نص الخطاب)"
            value={content}
            onChange={setContent}
            placeholders={PLACEHOLDERS}
            minHeight={320}
          />

          {/* Print layout: logo + header + footer (optional) */}
          <div className="border border-gray-200 rounded-lg p-4 space-y-4 bg-gray-50">
            <h3 className="text-sm font-semibold text-gray-800">
              Print Layout — Letterhead &amp; Footer (اختياري — ترويسة وتذييل الخطاب)
            </h3>
            <p className="text-xs text-gray-500">
              Leave empty to print the default PetroMarine header and no footer — اتركها فارغة لاستخدام الترويسة الافتراضية.
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Company Logo (لوجو الشركة)
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
                className="w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
              />
              {(logoFile || letter.logoUrl) && (
                <img
                  src={logoFile ? URL.createObjectURL(logoFile) : resolveFileUrl(letter.logoUrl)}
                  alt="Letter logo"
                  className="mt-2 h-16 object-contain border border-gray-200 rounded p-1 bg-white"
                />
              )}
            </div>
            <RichTextEditor
              label="Letter Header — prints above the letter (ترويسة الخطاب)"
              value={headerHtml}
              onChange={setHeaderHtml}
              dir="rtl"
              minHeight={80}
            />
            <RichTextEditor
              label="Letter Footer — prints at the bottom (تذييل الخطاب)"
              value={footerHtml}
              onChange={setFooterHtml}
              dir="rtl"
              minHeight={60}
            />
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button type="button" variant="primary" onClick={handleSave} disabled={busy}>
              {busy ? 'Saving...' : 'Save Template (حفظ)'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImmigrationLetterEditModal;
